/**
 * Sentry wrapper para pos-web. Opt-in: si `VITE_SENTRY_DSN` no está seteada
 * (dev default), `initSentry()` es no-op y ningún API de Sentry reporta.
 *
 * Uso:
 *   - `initSentry()` en `main.tsx` antes de `createRoot`.
 *   - `setSentryUser()` al login; `clearSentryUser()` al logout.
 *   - `<Sentry.ErrorBoundary>` (re-exportado como `SentryErrorBoundary`)
 *     envuelve el RouterProvider.
 */

import * as Sentry from '@sentry/react';

let initialized = false;

export const initSentry = (): void => {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
    release: import.meta.env.VITE_RELEASE,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
  });
  initialized = true;
};

export const setSentryUser = (
  userId: string,
  tenantId: string,
  role: string | null,
): void => {
  if (!initialized) return;
  Sentry.setUser({ id: userId });
  Sentry.setTag('tenantId', tenantId);
  if (role) Sentry.setTag('role', role);
};

export const clearSentryUser = (): void => {
  if (!initialized) return;
  Sentry.setUser(null);
};

export const SentryErrorBoundary = Sentry.ErrorBoundary;
